import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  Switch,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCalendarEvents,
  blockDates,
  unblockDates,
} from "src/actions/BookingActions";
import { toast } from "react-toastify";
import axios from "axios";
import {
  Close,
  Block,
  Delete,
  Sync,
  ChevronRight,
  Event,
} from "@material-ui/icons";

const APIURL = process.env.REACT_APP_API_URL;
const localizer = momentLocalizer(moment);

const PLATFORMS = {
  airbnb: { name: "Airbnb", color: "#FF5A5F", icon: "\u{1F3E0}" },
  vrbo: { name: "VRBO", color: "#3E59A8", icon: "\u{1F3E1}" },
  booking: { name: "Booking.com", color: "#003580", icon: "\u{1F3E8}" },
  google: { name: "Google Calendar", color: "#4285F4", icon: "\u{1F4C5}" },
  other: { name: "Other", color: "#6b7280", icon: "\u{1F517}" },
};

const EVENT_COLORS = {
  booking_awaiting_host: { bg: "#f59e0b", text: "#fff" },
  booking_confirmed: { bg: "#0d9488", text: "#fff" },
  manual_block: { bg: "#6b7280", text: "#fff" },
};

function getEventColor(event) {
  if (event.eventType === "manual_block") return EVENT_COLORS.manual_block;
  if (event.status === "confirmed") return EVENT_COLORS.booking_confirmed;
  return EVENT_COLORS.booking_awaiting_host;
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#f9fafb",
    minHeight: "100%",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  filterChips: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: 20,
  },
  chip: {
    borderRadius: 20,
    fontWeight: 500,
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 320px",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  calendarCard: {
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
    overflow: "hidden",
  },
  sidePanel: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  sideCard: {
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "none",
  },
  legend: {
    display: "flex",
    gap: theme.spacing(2.5),
    padding: "12px 20px",
    borderBottom: "1px solid #f3f4f6",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  legendBar: {
    width: 20,
    height: 4,
    borderRadius: 2,
  },
  connRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 0",
    borderBottom: "1px solid #f9fafb",
    "&:last-child": { borderBottom: "none" },
  },
  connIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  eventRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #f9fafb",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#fafafa" },
    "&:last-child": { borderBottom: "none" },
  },
  eventBar: {
    width: 3,
    height: 32,
    borderRadius: 2,
    flexShrink: 0,
  },
  emptyConnect: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    borderRadius: 10,
    border: "1px dashed #d1d5db",
    backgroundColor: "#fafafa",
    cursor: "pointer",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: "#0d9488" },
  },
  dialogTable: {
    "& .MuiTableCell-root": { padding: theme.spacing(1.5) },
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 12,
  },
}));

// ── Calendar event bubble ──

function CalendarEvent({ event }) {
  const label =
    event.eventType === "manual_block"
      ? `\u{1F6AB} ${event.homeTitle}`
      : `${event.title} \u00B7 ${event.homeTitle}`;

  return (
    <Box
      sx={{
        fontSize: 12,
        lineHeight: 1.3,
        px: 0.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

// ── Event detail dialog ──

function EventDetailDialog({ event, open, onClose, onUnblock }) {
  const classes = useStyles();
  if (!event) return null;

  const isBlock = event.eventType === "manual_block";
  const color = getEventColor(event);
  const statusLabel = isBlock
    ? "Manually Blocked"
    : event.status === "confirmed"
      ? "Confirmed"
      : "Awaiting Host";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isBlock ? "Blocked Dates" : "Booking Details"}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} variant="outlined">
          <Table className={classes.dialogTable}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>Property</strong>
                </TableCell>
                <TableCell>{event.homeTitle}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabel}
                    size="small"
                    className={classes.statusChip}
                    style={{ backgroundColor: color.bg, color: color.text }}
                  />
                </TableCell>
              </TableRow>
              {!isBlock && (
                <>
                  <TableRow>
                    <TableCell>
                      <strong>Guest</strong>
                    </TableCell>
                    <TableCell>
                      {event.guestName || "\u2014"} ({event.guestCount} guests)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Stay</strong>
                    </TableCell>
                    <TableCell>{event.description}</TableCell>
                  </TableRow>
                </>
              )}
              {isBlock && event.description && (
                <TableRow>
                  <TableCell>
                    <strong>Reason</strong>
                  </TableCell>
                  <TableCell>{event.description}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <strong>From</strong>
                </TableCell>
                <TableCell>
                  {moment(event.start).format("ddd, MMM D YYYY")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>To</strong>
                </TableCell>
                <TableCell>
                  {moment(event.end).format("ddd, MMM D YYYY")}
                </TableCell>
              </TableRow>
              {isBlock && (
                <TableRow>
                  <TableCell>
                    <strong>Action</strong>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<Delete />}
                      onClick={() => onUnblock(event)}
                    >
                      Unblock
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}

// ── Block dates dialog ──

function BlockDatesDialog({ open, onClose, homes, onSubmit }) {
  const [form, setForm] = useState({
    homeId: "",
    startDate: "",
    endDate: "",
    reason: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.homeId || !form.startDate || !form.endDate) {
      toast.warning("Please fill all required fields.");
      return;
    }
    onSubmit(form);
    setForm({ homeId: "", startDate: "", endDate: "", reason: "" });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Block Dates</Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Block dates when your property is unavailable — maintenance, personal
          use, renovation, etc.
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Select Property</InputLabel>
                <Select
                  name="homeId"
                  value={form.homeId}
                  onChange={handleChange}
                  label="Select Property"
                  required
                >
                  {homes.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason (optional)"
                name="reason"
                value={form.reason}
                onChange={handleChange}
                multiline
                minRows={2}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="e.g. Maintenance, personal use..."
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Block />}
                  style={{ backgroundColor: "#0d9488", color: "#fff" }}
                >
                  Block Dates
                </Button>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════

function HomeCalendar() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();

  const { events, homes, loading } = useSelector((state) => state.booking);

  const [filterHome, setFilterHome] = useState(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loadingSync, setLoadingSync] = useState(false);

  useEffect(() => {
    dispatch(fetchCalendarEvents());
    fetchConnections();
  }, [dispatch]);

  const fetchConnections = async () => {
    try {
      setLoadingSync(true);
      const res = await axios.get(`${APIURL}homes/host/calendar/sync/`);
      setConnections(res.data.connections || []);
    } catch (err) {
      // Silent — sync section is secondary
    } finally {
      setLoadingSync(false);
    }
  };

  const filteredEvents = filterHome
    ? events.filter((e) => e.homeId === filterHome)
    : events;

  const upcomingEvents = [...filteredEvents]
    .filter((e) => new Date(e.start) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 6);

  const eventStyleGetter = useCallback((event) => {
    const color = getEventColor(event);
    return {
      style: {
        backgroundColor: color.bg,
        color: color.text,
        borderRadius: 4,
        border: "none",
        fontSize: 12,
        padding: "2px 6px",
      },
    };
  }, []);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDetailOpen(true);
  };

  const handleBlockSubmit = (form) => {
    dispatch(blockDates(form));
    setBlockDialogOpen(false);
  };

  const handleUnblock = (event) => {
    const msg = `Unblock ${event.homeTitle} from ${moment(event.start).format("MMM D")} to ${moment(event.end).format("MMM D")}?`;
    if (window.confirm(msg)) {
      dispatch(
        unblockDates({
          homeId: event.homeId,
          startDate: moment(event.start).format("YYYY-MM-DD"),
          endDate: moment(event.end).format("YYYY-MM-DD"),
        }),
      );
      setDetailOpen(false);
    }
  };

  const handleRefreshSync = async (connId) => {
    try {
      await axios.post(`${APIURL}homes/host/calendar/sync/${connId}/refresh/`);
      toast.success("Calendar synced.");
      fetchConnections();
      dispatch(fetchCalendarEvents());
    } catch (err) {
      toast.error("Sync failed.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Tubayo Host | Calendar</title>
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth={false}>
          {/* ── Top bar ── */}
          <Box className={classes.topBar}>
            <Box>
              <Typography variant="h5" style={{ fontWeight: 600 }}>
                Calendar
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Manage availability across your properties
              </Typography>
            </Box>
            <Box display="flex" style={{ gap: 8 }}>
              <Button
                variant="outlined"
                startIcon={<Block />}
                onClick={() => setBlockDialogOpen(true)}
                style={{ borderColor: "#d1d5db", color: "#374151" }}
              >
                Block Dates
              </Button>
              <Button
                variant="contained"
                startIcon={<Sync />}
                onClick={() => navigate("/app/calendarSync")}
                style={{ backgroundColor: "#0d9488", color: "#fff" }}
              >
                Sync Settings
              </Button>
            </Box>
          </Box>

          {/* ── Property chips ── */}
          <Box className={classes.filterChips}>
            <Chip
              label="All"
              className={classes.chip}
              onClick={() => setFilterHome(null)}
              style={
                filterHome === null
                  ? { backgroundColor: "#0d9488", color: "#fff" }
                  : { backgroundColor: "#f3f4f6", color: "#374151" }
              }
            />
            {homes.map((h) => (
              <Chip
                key={h.id}
                label={h.title}
                className={classes.chip}
                onClick={() => setFilterHome(h.id)}
                style={
                  filterHome === h.id
                    ? { backgroundColor: "#0d9488", color: "#fff" }
                    : { backgroundColor: "#f3f4f6", color: "#374151" }
                }
              />
            ))}
            {loading && <CircularProgress size={18} />}
          </Box>

          {/* ── Two-column layout ── */}
          <Box className={classes.mainGrid}>
            {/* LEFT — Calendar */}
            <Card className={classes.calendarCard}>
              <Box className={classes.legend}>
                {[
                  { color: "#0d9488", label: "Confirmed" },
                  { color: "#f59e0b", label: "Pending" },
                  { color: "#6b7280", label: "Blocked" },
                ].map((item) => (
                  <Box key={item.label} className={classes.legendItem}>
                    <span
                      className={classes.legendBar}
                      style={{ backgroundColor: item.color }}
                    />
                    <Typography
                      variant="caption"
                      style={{ color: "#9ca3af", fontWeight: 500 }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <CardContent>
                <Box
                  sx={{
                    "& .rbc-today": {
                      backgroundColor: "rgba(13,148,136,0.05)",
                    },
                    "& .rbc-off-range-bg": { backgroundColor: "#fafafa" },
                    "& .rbc-header": {
                      padding: "10px 4px",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#6b7280",
                      borderBottom: "1px solid #f3f4f6",
                    },
                    "& .rbc-event": { borderRadius: "4px !important" },
                    "& .rbc-month-view": { border: "none" },
                    "& .rbc-day-bg + .rbc-day-bg, & .rbc-month-row + .rbc-month-row":
                      { borderColor: "#f3f4f6" },
                  }}
                >
                  <Calendar
                    localizer={localizer}
                    events={filteredEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: 540 }}
                    views={["month", "week", "agenda"]}
                    defaultView="month"
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleSelectEvent}
                    components={{ event: CalendarEvent }}
                    popup
                  />
                </Box>
              </CardContent>
            </Card>

            {/* RIGHT — Side panel */}
            <Box className={classes.sidePanel}>
              {/* Connected calendars */}
              <Card className={classes.sideCard}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Connected calendars
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color: "#0d9488",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => navigate("/app/calendarSync")}
                    >
                      Manage
                    </Typography>
                  </Box>

                  {loadingSync ? (
                    <Box py={2} textAlign="center">
                      <CircularProgress size={20} />
                    </Box>
                  ) : connections.length === 0 ? (
                    <Box
                      className={classes.emptyConnect}
                      onClick={() => navigate("/app/calendarSync")}
                    >
                      <span style={{ fontSize: 22 }}>{"\u{1F517}"}</span>
                      <Box flex={1}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, fontSize: 13 }}
                        >
                          Sync with Airbnb, VRBO & more
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: "#9ca3af" }}
                        >
                          Avoid double bookings
                        </Typography>
                      </Box>
                      <ChevronRight
                        style={{ color: "#d1d5db", fontSize: 20 }}
                      />
                    </Box>
                  ) : (
                    connections.map((conn) => {
                      const meta = PLATFORMS[conn.platform] || PLATFORMS.other;
                      const statusColor =
                        conn.last_sync_status === "success"
                          ? "#10b981"
                          : conn.last_sync_status === "error"
                            ? "#ef4444"
                            : "#f59e0b";

                      return (
                        <Box key={conn.id} className={classes.connRow}>
                          <Box
                            className={classes.connIcon}
                            style={{ backgroundColor: meta.color + "12" }}
                          >
                            {meta.icon}
                          </Box>
                          <Box flex={1} minWidth={0}>
                            <Typography
                              variant="body2"
                              style={{
                                fontWeight: 500,
                                fontSize: 14,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {conn.label || meta.name}
                            </Typography>
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ gap: 4 }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: statusColor,
                                  display: "inline-block",
                                }}
                              />
                              <Typography
                                variant="caption"
                                style={{ color: "#9ca3af" }}
                              >
                                {conn.events_synced} events
                              </Typography>
                            </Box>
                          </Box>
                          <Tooltip title="Sync now">
                            <IconButton
                              size="small"
                              onClick={() => handleRefreshSync(conn.id)}
                            >
                              <Sync
                                style={{ fontSize: 16, color: "#0d9488" }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Switch
                            size="small"
                            checked={conn.is_active}
                            onChange={() => handleRefreshSync(conn.id)}
                            color="primary"
                          />
                        </Box>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Upcoming events */}
              <Card className={classes.sideCard}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Upcoming
                    </Typography>
                    <Typography variant="caption" style={{ color: "#9ca3af" }}>
                      {upcomingEvents.length} events
                    </Typography>
                  </Box>

                  {upcomingEvents.length === 0 ? (
                    <Box py={2} textAlign="center">
                      <Event style={{ fontSize: 32, color: "#e5e7eb" }} />
                      <Typography
                        variant="body2"
                        style={{ color: "#9ca3af", marginTop: 4 }}
                      >
                        No upcoming events
                      </Typography>
                    </Box>
                  ) : (
                    upcomingEvents.map((evt, idx) => {
                      const isBlock = evt.eventType === "manual_block";
                      const barColor = isBlock
                        ? "#6b7280"
                        : evt.status === "confirmed"
                          ? "#0d9488"
                          : "#f59e0b";

                      return (
                        <Box
                          key={idx}
                          className={classes.eventRow}
                          onClick={() => handleSelectEvent(evt)}
                        >
                          <span
                            className={classes.eventBar}
                            style={{ backgroundColor: barColor }}
                          />
                          <Box flex={1} minWidth={0}>
                            <Typography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                fontSize: 13,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {isBlock
                                ? `Blocked \u00B7 ${evt.homeTitle}`
                                : `${evt.title} \u00B7 ${evt.homeTitle}`}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ color: "#9ca3af" }}
                            >
                              {moment(evt.start).format("MMM D")} {"\u2192"}{" "}
                              {moment(evt.end).format("MMM D")}
                              {evt.description && !isBlock
                                ? ` \u00B7 ${evt.description}`
                                : ""}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>
      </Box>

      <BlockDatesDialog
        open={blockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
        homes={homes}
        onSubmit={handleBlockSubmit}
      />
      <EventDetailDialog
        event={selectedEvent}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUnblock={handleUnblock}
      />
    </>
  );
}

export default HomeCalendar;
